import { FC, useEffect, useState } from "react"
import { Actions, ControlProps } from "@jsonforms/core"
import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react"
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
} from "@mui/material"
import InputSummary from "../NewForm/InputSummary"
import { generateSummary } from "../NewForm/formUtils"

interface InputProps {
  width: number
  key: string
  const: string
  errorMsg: string
  multiline?: boolean
  minRows?: number
  isNum?: boolean
  startLabel?: string
  endLabel?: string
  showInSameLine?: boolean
  startAdornment?: string
  placeholder?: string
  isOptional?: boolean
}

const UApprovalCheckbox: FC<ControlProps> = (props) => {
  const { schema, path, label, data, visible, handleChange } = props
  // this control is an approval control with a single checkbox
  // approval is stored as a string 'yes'/'no' when schema.type === 'string'
  const [errors, setErrors] = useState("")
  const { core, dispatch } = useJsonForms()

  const handleCheckboxChange = (checkbox: any) => {
    // Approval is stored as a string 'yes'/'no'. Toggle it on click.
    const currentVal = data
    const isYes = typeof currentVal === "string" && currentVal.toLowerCase() === "yes"
    const newVal = isYes ? "no" : "yes"
    handleChange(path, newVal)
  }


  const updateErrors = (message: string) => {
    setErrors(message)
    dispatch &&
      dispatch(
        Actions.updateErrors([
          {
            instancePath: "/" + path,
            message,
            schemaPath: "",
            keyword: "",
            params: {},
          },
        ]),
      )
  }

  const validateCustomField = () => {
    const val = data
    const isRequired = core?.schema?.required?.indexOf(path) !== -1
    const isEmpty = String(val).trim() === ""

    if (!isRequired && isEmpty) {
      updateErrors("")
      return
    }

    if (isEmpty) {
      updateErrors("This is a required field")
      return
    }

    updateErrors("")
  }

  useEffect(() => {
    if (!visible) {
      // reset approval to 'no' when hidden
      handleChange(path, "no")
      updateErrors("")
    }
  }, [visible])

  useEffect(() => {
    validateCustomField()
  }, [data, core?.validationMode])

  useEffect(() =>{
    if (schema?.summary) return
    const pontics = core?.data?.pontics?.value?.[0]?.toLowerCase()
    const biteRamp = core?.data?.add_bite_ramps?.bite_ramp
    const biteRampHasValues = Boolean(
      biteRamp &&
        Object.values(biteRamp).some((v: any) => Array.isArray(v) ? v.length > 0 : false),
    )

    if (pontics === "yes" || biteRampHasValues) handleChange(path, "yes")

  }, [core?.data?.pontics, core?.data?.add_bite_ramps?.bite_ramp])

  const item: any = schema?.checkboxes[0]
  const currentVal = typeof data === "string" && data
  const checked = typeof currentVal === "string" && currentVal.toLowerCase() === "yes"

  return (
    <>
      {visible && schema?.summary ? (
        <InputSummary
          title={data.toLowerCase() === "yes" ? schema?.summaryTitle : schema?.title}
          label={schema?.checkboxes[0].label}
          inputVal={generateSummary(schema, data)}
        />
      ) : (
        visible && (
          <FormControl
            error={
              errors && core?.validationMode === "ValidateAndShow"
                ? true
                : false
            }
            component="fieldset"
            variant="standard"
             sx={{ my: 1, display: "flex", flexDirection: "column", pl: 1 }}
          >
            <FormLabel
              sx={{
                width: "100%",
                marginTop: 1,
                // v2:
                // minWidth: 200,
                color: "text.secondary",
                "&.Mui-focused": {
                  color: "rgba(0, 0, 0, 0.60)",
                },
              }}
            >
              {schema?.title}
            </FormLabel>
            <Box>
              <FormGroup sx={{ pl: 1 }}>
                {/* Single approval checkbox (we only expect one) */}
                <Box>
                  <Box>
                    <FormControlLabel
                      sx={{
                        display: "inline-flex",
                        pointerEvents: "none",
                        "& .MuiFormControlLabel-label": {
                          pointerEvents: "none",
                        },
                      }}
                      key={item.value}
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={() => {
                            handleCheckboxChange(item)
                          }}
                          sx={{
                            pointerEvents: "auto",
                          }}
                          name={item.value}
                        />
                      }
                      label={
                        <span
                          style={{
                            display: "inline-flex",
                            pointerEvents: "auto",
                            flexDirection: "column",
                          }}
                        >
                        <span style={{paddingTop: "18px"}}>{item.label}</span>
                          {!!item?.labelSecondary && (
                            <span
                              style={{ fontWeight: 400, color: "#666"}}
                            >
                              {item?.labelSecondary}
                            </span>
                          )}
                        </span>
                      }
                    />
                  </Box>
                </Box>
                {errors && core?.validationMode === "ValidateAndShow" && (
                  <FormHelperText sx={{ marginLeft: 0 }}>
                    {errors}
                  </FormHelperText>
                )}
              </FormGroup>
            </Box>
          </FormControl>
        )
      )}
    </>
  )
}

export default withJsonFormsControlProps(UApprovalCheckbox)
